'use client';

import { format, formatDistanceToNowStrict } from 'date-fns';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Histogram, type HistogramData, type HistogramTone } from '@charts';
import { useLifeEvents } from '@/data/sources/lifeEvents';
import { ContextualPopup, Divider, Flex, Typography } from '@/ui';
import styles from './LifePulse.module.css';

const MODES = [
  'gaming',
  'fitness',
  'reading',
  'learning',
  'dnd',
  'work',
  'sleep',
] as const;
type Mode = (typeof MODES)[number];

const COLORS: Record<Mode, string> = {
  work: '#cfcfcf',
  dnd: '#a6a6a6',
  sleep: '#828282',
  fitness: '#34c759',
  learning: '#007aff',
  reading: '#ff9500',
  gaming: '#ff3b30',
};

const LABELS: Record<Mode, string> = {
  gaming: 'Gaming',
  fitness: 'Fitness',
  reading: 'Reading',
  learning: 'Learning',
  dnd: 'DND',
  work: 'Work',
  sleep: 'Sleep',
};

const HIDDEN_MODES: Mode[] = ['work', 'dnd', 'sleep'];
const VISIBLE_MODES = MODES.filter((mode) => !HIDDEN_MODES.includes(mode));

const RESOLUTION = '24h';
const WINDOW_DAYS = 182;
const BUCKET_SECONDS = 86400;

const TRACKED_SINCE = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Prague',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

interface PulseBucket {
  start: number;
  minutes: Record<string, number>;
}

interface PulseResponse {
  buckets: PulseBucket[];
  /** epoch seconds of the most recent focus sync */
  lastSync: number | null;
}

const fetchPulse = async (): Promise<PulseResponse> => {
  const res = await fetch(`/api/life/pulse?resolution=${RESOLUTION}`);
  return res.json();
};

const firstTracked = (buckets: PulseBucket[]) =>
  buckets.find((bucket) =>
    MODES.some((mode) => (bucket.minutes[mode] ?? 0) > 0)
  )?.start ?? null;

const toHistogramData = (buckets: PulseBucket[]): HistogramData => {
  const tones: HistogramTone[] = VISIBLE_MODES.map((mode) => ({
    id: mode,
    color: COLORS[mode],
  }));
  const end = Date.now() / 1000;
  // A fixed rolling window (not fitted to the data) so the weekly ticks extend
  // back past the first tracked day — the axis reads as a continuing timeline
  // with recent activity on the right, rather than all there is.
  const start = end - WINDOW_DAYS * 86400;

  return {
    start,
    end,
    series: VISIBLE_MODES.map((mode) => ({
      id: mode,
      tone: mode,
      points: buckets
        .filter((bucket) => (bucket.minutes[mode] ?? 0) > 0)
        .map(
          (bucket) => [bucket.start, bucket.minutes[mode]] as [number, number]
        ),
    })),
    tones,
  };
};

export const LifePulse = ({ play = true }: { play?: boolean }) => {
  const { data } = useQuery({
    queryKey: ['lifePulse', RESOLUTION],
    queryFn: fetchPulse,
  });
  const buckets = data?.buckets;

  // The freshness read comes from the live events source (polled ~30s), not the
  // hour-cached pulse aggregation, so "Last sync" reflects real recency.
  const { data: life } = useLifeEvents('day');

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<Mode | null>(null);
  const filterIds = active ? [active] : null;

  const trackedSince = buckets?.length ? firstTracked(buckets) : null;
  const lastSync = life?.events?.length
    ? Math.max(...life.events.map((event) => event.receivedAt.getTime()))
    : null;

  return (
    <ContextualPopup
      behaviour="click"
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setActive(null);
      }}
      handle={
        <div
          style={{
            minHeight: 26,
            filter: open ? undefined : 'grayscale(1)',
            transition: 'filter 300ms ease',
          }}
        >
          {play && buckets && buckets.length > 0 && (
            <Histogram
              data={toHistogramData(buckets)}
              bucketSeconds={BUCKET_SECONDS}
              filterIds={filterIds}
              tickUnit="month"
              height={20}
              ariaLabel="My life pulse"
            />
          )}
        </div>
      }
      handleProps={{ style: { width: '100%', pointerEvents: 'auto' } }}
    >
      <ContextualPopup.Section>
        <Flex flexDirection="column" gap={8}>
          <Flex flexDirection="column" gap={4}>
            <Typography.Caption size="L">My life pulse</Typography.Caption>
            <Typography.Caption opacity={0.5} textAlign="left">
              Each bar represents a single day in my life. Activities are
              tracked automatically based on my Apple devices focus modes (based
              on location, activity, used apps, and other heuristics).
            </Typography.Caption>
          </Flex>

          <div className={styles.filters}>
            {VISIBLE_MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                className={
                  active && active !== mode ? styles.filterOff : styles.filter
                }
                onClick={() => setActive(active === mode ? null : mode)}
              >
                <span
                  className={styles.dot}
                  style={{ background: COLORS[mode] }}
                />
                {LABELS[mode]}
              </button>
            ))}
          </div>
        </Flex>
      </ContextualPopup.Section>

      {lastSync != null && (
        <>
          <Divider.Horizontal />

          <ContextualPopup.Section>
            <Flex justifyContent="space-between" alignItems="center">
              <Typography.Caption size="L">Last sync</Typography.Caption>

              <Flex flexDirection="column" alignItems="end">
                <Typography.Caption size="L">
                  {formatDistanceToNowStrict(lastSync, {
                    addSuffix: true,
                  })}
                </Typography.Caption>

                <Typography.Caption opacity={0.5} textAlign="left">
                  {format(lastSync, 'dd. MMM. yyyy, p')}
                </Typography.Caption>
              </Flex>
            </Flex>
          </ContextualPopup.Section>
        </>
      )}

      {trackedSince != null && (
        <>
          <Divider.Horizontal />

          <ContextualPopup.Section>
            <Typography.Caption opacity={0.5} textAlign="left">
              Tracked since {TRACKED_SINCE.format(trackedSince * 1000)}.
            </Typography.Caption>
          </ContextualPopup.Section>
        </>
      )}
    </ContextualPopup>
  );
};
